import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { useEntity } from '../../contexts/EntityContext';
import { useToast } from '../ui/Toast';
import { api, type Wallet, type Client } from '../../lib/api';
import { ClientSearchAutocomplete } from './ClientSearchAutocomplete';
import { isCashierEntityAllowed } from './constants';

interface OwnerOperationFormProps {
  onSubmit: () => void;
}

type Direction = 'deposit' | 'withdrawal';

const ownerFormSchema = z.object({
  direction: z.enum(['deposit', 'withdrawal']),
  amount: z
    .string()
    .min(1, 'Amount is required')
    .refine(
      (val) => !isNaN(parseFloat(val)) && parseFloat(val) > 0,
      'Must be a positive number'
    ),
  walletId: z.string().min(1, 'Please select a wallet'),
  clientId: z.string().min(1, 'Client is required'),
  date: z.string().optional(),
  comment: z.string().max(200).optional(),
});

type OwnerFormData = z.infer<typeof ownerFormSchema>;

const CURRENCY_ICONS: Record<string, string> = {
  RUB: '₽',
  EUR: '€',
  USD: '$',
  GBP: '£',
  USDT: '₮',
  BTC: '₿',
  ETH: 'Ξ',
  USDC: '$',
};

export function OwnerOperationForm({ onSubmit }: OwnerOperationFormProps) {
  const { t } = useTranslation();
  const { currentEntity } = useEntity();
  const toast = useToast();

  const [wallets, setWallets] = useState<Wallet[]>([]);
  const [walletsLoading, setWalletsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    clearErrors,
    formState: { errors },
  } = useForm<OwnerFormData>({
    resolver: zodResolver(ownerFormSchema),
    defaultValues: {
      direction: 'deposit',
      amount: '',
      walletId: '',
      clientId: '',
      date: new Date().toISOString().split('T')[0],
      comment: '',
    },
  });

  const direction = watch('direction');
  const walletId = watch('walletId');

  // Derive currency from selected wallet
  const selectedWallet = wallets.find((w) => w.id === walletId);
  const currency = selectedWallet?.currencyCode || '';

  const fetchWallets = useCallback(async () => {
    if (!currentEntity?.id) return;
    if (!isCashierEntityAllowed(currentEntity.code)) {
      setWallets([]);
      setWalletsLoading(false);
      return;
    }
    try {
      setWalletsLoading(true);
      const result = await api.admin.wallets.list(currentEntity.id);
      setWallets(result.data.filter((w) => w.isActive));
    } catch {
      toast.error('Failed to load wallets');
    } finally {
      setWalletsLoading(false);
    }
  }, [currentEntity?.id, currentEntity?.code, toast]);

  useEffect(() => {
    fetchWallets();
  }, [fetchWallets]);

  const handleDirectionChange = (newDirection: Direction) => {
    setValue('direction', newDirection);
  };

  const onFormSubmit = async (data: OwnerFormData) => {
    if (!currentEntity?.id) {
      toast.error('No entity selected');
      return;
    }

    setIsSubmitting(true);
    try {
      await api.transactions.create(currentEntity.id, {
        type: data.direction, // 'deposit' or 'withdrawal'
        amount: data.amount,
        currency,
        walletId: data.walletId,
        clientId: data.clientId,
        ...(data.comment && { comment: data.comment }),
        ...(data.date && { transactionDate: data.date }),
      });

      const successKey = data.direction === 'deposit'
        ? 'cashier.createOperation.ownerDepositSuccess'
        : 'cashier.createOperation.ownerWithdrawalSuccess';
      const fallback = data.direction === 'deposit' ? 'Deposit recorded' : 'Withdrawal recorded';
      toast.success(t(successKey, fallback));

      // Reset form but keep wallet and direction
      const prevDirection = data.direction;
      const prevWalletId = data.walletId;
      reset({
        direction: prevDirection,
        amount: '',
        walletId: prevWalletId,
        clientId: '',
        date: new Date().toISOString().split('T')[0],
        comment: '',
      });
      setSelectedClient(null);

      onSubmit();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to record operation');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDeposit = direction === 'deposit';

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="p-6 space-y-5">
      {/* Direction Toggle */}
      <div>
        <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
          {t('cashier.createOperation.ownerDirection', 'Direction')}
        </label>
        <div className="flex gap-2">
          <button
            type="button"
            className={`
              flex-1 flex items-center justify-center gap-2 py-3 px-4
              rounded-lg text-sm font-bold cursor-pointer transition-all border
              ${isDeposit
                ? 'bg-ctp-green/10 text-ctp-green border-ctp-green/40 shadow-sm'
                : 'bg-surface text-foreground-muted border-border hover:text-foreground hover:border-foreground-muted'
              }
            `}
            onClick={() => handleDirectionChange('deposit')}
          >
            <ArrowDownLeft size={18} />
            {t('cashier.createOperation.ownerDeposit', 'DEPOSIT (IN)')}
          </button>
          <button
            type="button"
            className={`
              flex-1 flex items-center justify-center gap-2 py-3 px-4
              rounded-lg text-sm font-bold cursor-pointer transition-all border
              ${!isDeposit
                ? 'bg-ctp-red/10 text-ctp-red border-ctp-red/40 shadow-sm'
                : 'bg-surface text-foreground-muted border-border hover:text-foreground hover:border-foreground-muted'
              }
            `}
            onClick={() => handleDirectionChange('withdrawal')}
          >
            <ArrowUpRight size={18} />
            {t('cashier.createOperation.ownerWithdrawal', 'WITHDRAWAL (OUT)')}
          </button>
        </div>
      </div>

      {/* Amount + Wallet */}
      <div>
        <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
          {t('cashier.createOperation.ownerAmount', 'Amount')}
        </label>
        <div className="flex gap-3">
          {/* Amount input */}
          <div className="flex-1">
            <div className="relative">
              {currency && (
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm font-medium text-foreground-muted pointer-events-none">
                  {CURRENCY_ICONS[currency.toUpperCase()] || currency}
                </span>
              )}
              <input
                type="text"
                inputMode="decimal"
                className={`
                  w-full py-3 border rounded-lg text-sm text-foreground bg-surface
                  focus:outline-none placeholder:text-foreground-muted
                  ${currency ? 'pl-8 pr-3' : 'px-3'}
                  ${errors.amount ? 'border-ctp-red' : 'border-border'}
                  ${isDeposit ? 'focus:border-ctp-green' : 'focus:border-ctp-red'}
                `}
                placeholder="0.00"
                {...register('amount')}
              />
            </div>
            {errors.amount && (
              <p className="text-[11px] text-ctp-red mt-1">{errors.amount.message}</p>
            )}
          </div>

          {/* Wallet selector */}
          <div className="w-48">
            <select
              className={`
                w-full py-3 px-3 border rounded-lg text-sm text-foreground bg-surface
                focus:outline-none
                ${errors.walletId ? 'border-ctp-red' : 'border-border'}
                ${isDeposit ? 'focus:border-ctp-green' : 'focus:border-ctp-red'}
              `}
              {...register('walletId')}
            >
              <option value="">
                {walletsLoading
                  ? t('loading', 'Loading...')
                  : t('cashier.createOperation.ownerWallet', 'Select Wallet')}
              </option>
              {wallets.map((w) => (
                <option key={w.id} value={w.id}>
                  {CURRENCY_ICONS[w.currencyCode.toUpperCase()] || w.currencyCode}{' '}
                  {w.name}
                </option>
              ))}
            </select>
            {errors.walletId && (
              <p className="text-[11px] text-ctp-red mt-1">{errors.walletId.message}</p>
            )}
          </div>
        </div>
      </div>

      {/* Client Search */}
      <div>
        <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
          {t('cashier.createOperation.ownerClient', 'Client (Owner)')}
        </label>
        <ClientSearchAutocomplete
          value={watch('clientId')}
          onChange={(clientId) => setValue('clientId', clientId)}
          onClientSelect={setSelectedClient}
          onClearError={() => clearErrors('clientId')}
          error={errors.clientId?.message}
          placeholder={t('cashier.createOperation.ownerClientPlaceholder', 'Search or type owner name...')}
        />
        {selectedClient && (
          <p className="text-[11px] text-foreground-muted mt-1.5">
            {t('cashier.createOperation.ownerClientSelected', 'Selected')}: <strong>{selectedClient.name}</strong>
          </p>
        )}
      </div>

      {/* Date + Comment row */}
      <div className="flex flex-col sm:flex-row gap-4">
        {/* Date */}
        <div className="sm:w-44">
          <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
            {t('cashier.createOperation.date', 'Date')}
          </label>
          <input
            type="date"
            className={`w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none ${isDeposit ? 'focus:border-ctp-green' : 'focus:border-ctp-red'}`}
            {...register('date')}
          />
          <p className="text-[11px] text-foreground-muted mt-1.5">
            {t('cashier.createOperation.dateHint', 'Optional. Defaults to today.')}
          </p>
        </div>

        {/* Comment */}
        <div className="flex-1">
          <label className="text-xs font-medium text-foreground-muted uppercase tracking-wide mb-2 block">
            {t('cashier.createOperation.comment', 'Comment')}
          </label>
          <input
            type="text"
            className={`w-full py-2.5 px-3 border border-border rounded-lg text-sm text-foreground bg-surface focus:outline-none placeholder:text-foreground-muted ${isDeposit ? 'focus:border-ctp-green' : 'focus:border-ctp-red'}`}
            placeholder={t('cashier.createOperation.addNote', 'Add a note...')}
            maxLength={200}
            {...register('comment')}
          />
        </div>
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={isSubmitting || walletsLoading}
        className={`
          flex items-center justify-center gap-2.5 w-full py-4 px-6
          text-white border-none rounded-xl text-[15px] font-bold
          cursor-pointer transition-all active:scale-95 mt-2 shadow-lg
          disabled:opacity-60 disabled:cursor-not-allowed
          ${isDeposit
            ? 'bg-gradient-to-r from-green-600 to-emerald-500 hover:from-green-700 hover:to-emerald-600'
            : 'bg-gradient-to-r from-red-600 to-rose-500 hover:from-red-700 hover:to-rose-600'
          }
        `}
      >
        {isDeposit ? <ArrowDownLeft size={18} /> : <ArrowUpRight size={18} />}
        {isSubmitting
          ? t('cashier.createOperation.processing', 'Processing...')
          : isDeposit
            ? t('cashier.createOperation.ownerDepositSubmit', 'RECORD DEPOSIT')
            : t('cashier.createOperation.ownerWithdrawalSubmit', 'RECORD WITHDRAWAL')
        }
      </button>
    </form>
  );
}
